import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export type PendingAccessCodeStatus = "idle" | "redeeming" | "success";

interface PendingAccessCodeStatusDialogProps {
  status: PendingAccessCodeStatus;
  onDismiss: () => void;
}

/**
 * How long the student waits before we offer a way out.
 *
 * The redeeming state is deliberately non-dismissable — no close button, outside-click
 * prevented, Escape ignored — so before this existed, a slow request left an org/school
 * learner with no exit but reloading the page, on their first experience of the product.
 * The request itself now times out at 20s; this gives them agency sooner.
 */
const SLOW_REDEEM_MS = 8_000;

export default function PendingAccessCodeStatusDialog({
  status,
  onDismiss,
}: PendingAccessCodeStatusDialogProps) {
  const { t } = useTranslation();
  const open = status !== "idle";
  const isSuccess = status === "success";
  const isRedeeming = status === "redeeming";
  const [isSlow, setIsSlow] = useState(false);

  useEffect(() => {
    if (!isRedeeming) {
      setIsSlow(false);
      return;
    }
    const id = setTimeout(() => setIsSlow(true), SLOW_REDEEM_MS);
    return () => clearTimeout(id);
  }, [isRedeeming]);

  // Once we have offered an exit, honour every way of taking it — Escape and
  // outside-click included, not just the button.
  const isDismissable = isSuccess || (isRedeeming && isSlow);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && isDismissable) onDismiss();
      }}
    >
      <DialogContent
        onPointerDownOutside={(event) => {
          if (!isDismissable) event.preventDefault();
        }}
        className={cn(
          "w-[calc(100vw-2rem)] max-w-sm rounded-lg bg-white p-6 text-center shadow-lg",
          // The built-in close X stays hidden until an exit is actually offered.
          !isDismissable && "[&>button]:hidden"
        )}
      >
        <DialogHeader className="items-center text-center">
          {isSuccess ? (
            <CheckCircle2 className="h-12 w-12 text-[#5DB075]" />
          ) : (
            <Loader2 className="h-12 w-12 animate-spin text-[#5DB075]" />
          )}
          <DialogTitle className="mt-4 text-xl font-semibold text-zinc-950">
            {isSuccess
              ? t("redeem.status_success_title")
              : t("redeem.status_loading_title")}
          </DialogTitle>
          <DialogDescription className="mt-2 text-center text-sm leading-6 text-zinc-600">
            {isSuccess
              ? t("redeem.status_success_body")
              : t("redeem.status_loading_body")}
          </DialogDescription>
        </DialogHeader>
        {isRedeeming && isSlow && (
          <p className="mt-3 text-center text-sm leading-6 text-zinc-600">
            {t("redeem.status_slow_body")}
          </p>
        )}
        {(isSuccess || (isRedeeming && isSlow)) && (
          <DialogFooter className="mt-2 sm:justify-center">
            <button
              type="button"
              onClick={onDismiss}
              className={cn(
                "w-full rounded-full px-5 py-3 text-sm font-semibold transition-colors sm:w-auto",
                isSuccess
                  ? "bg-[var(--dp-brand-button)] text-white hover:bg-[var(--dp-brand-hover)]"
                  : "border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50"
              )}
            >
              {isSuccess ? t("common.continue") : t("common.close")}
            </button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
